import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { fetchBrandConfig, buildEngagementConfig } from '../services/brandConfig';
import { APP_CONFIG } from '../config/appConfig';

const BrandConfigContext = createContext(null);

export const useBrandConfig = () => {
  const ctx = useContext(BrandConfigContext);
  return ctx ?? buildEngagementConfig(null, APP_CONFIG);
};

export const BrandConfigProvider = ({ children }) => {
  const [config, setConfig] = useState(() => buildEngagementConfig(null, APP_CONFIG));

  const load = useCallback(() => {
    fetchBrandConfig().then((data) => {
      const cfg = buildEngagementConfig(data, APP_CONFIG);
      setConfig(cfg);

      // Apply dynamic favicon
      if (data?.favicon_url) {
        let link = document.querySelector("link[rel='shortcut icon']") || document.querySelector("link[rel='icon']");
        if (!link) { link = document.createElement('link'); link.rel = 'shortcut icon'; document.head.appendChild(link); }
        link.href = data.favicon_url;
      }

      // Apply dynamic page title and meta tags
      if (data?.app_name) {
        document.title = data.app_name;
        const setMeta = (selector, value) => {
          const el = document.querySelector(selector);
          if (el) el.setAttribute('content', value);
        };
        setMeta('meta[name="apple-mobile-web-app-title"]', data.app_name);
        setMeta('meta[property="og:title"]', data.app_name);
        setMeta('meta[name="twitter:title"]', data.app_name);
      }

      // Apply dynamic theme color
      if (data?.theme?.primaryColor) {
        const setMeta = (selector, value) => {
          const el = document.querySelector(selector);
          if (el) el.setAttribute('content', value);
        };
        setMeta('meta[name="theme-color"]', data.theme.primaryColor);
      }
    });
  }, []);

  useEffect(() => {
    // Fetch on mount
    load();

    // Re-fetch when tab becomes visible (picks up changes saved from the dashboard)
    const onVisible = () => { if (document.visibilityState === 'visible') load(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [load]);

  return (
    <BrandConfigContext.Provider value={config}>
      {children}
    </BrandConfigContext.Provider>
  );
};
